import { ISearchVehiclesRequest } from 'types/vehicleTypes'
import { IPurchaseHistoryRequest } from 'types/purchaseHistoryTypes'
import { ISortBy, SortOrder } from 'types/baseTypes'
import { DateTime } from 'luxon'

export const BMW_VENDOR_CODE = 'BMWUS001'
export const NISSAN_VENDOR_CODE = 'NISUS001'
export const PageSizeOptions = [25, 50, 100]
export const RECORDS_PER_PAGE_TEXT = 'Records per page:'
export const DEFAULT_PAGE_SIZE = 25
export const DEFAULT_BIG_PAGE_SIZE = 50
export const DEFAULT_HUGE_PAGE_SIZE = 100
export const DEFAULT_SMALL_PAGE_SIZE = 5
export const CONTINUE = 'Continue'
export const SUBMIT = 'Submit'
export const BMW_TEXT = 'BMW'
export const MEDIUM_SLIDER_ITEM_SIZE = 4
export const BIG_SLIDER_ITEM_SIZE = 8
export const HUGE_SLIDER_ITEM_SIZE = 12
export const VEHICLE_PROPERTIES_TO_HIDE = ['DD_CONDITION', 'MMR', 'VEHICLE_LOCATION', 'SALE_CHANNEL']
export const DEFAULT_ERROR_MESSAGE = 'An error has occurred. Please try again.'
export const PAGINATION_ITEM_SIZE = 10
export const CSRF_HEADER = '__RequestVerificationToken'
export const CSRF_TOKEN = 'RequestVerificationToken'
export const TERM_AND_CONDITION = 'Terms And Conditions'
export const FAQ = 'FAQs'
export const FRAME_DAMAGE_LABEL = '<b>Frame Damage: </b>'
export const ANNOUNCEMENTS_VDP_LABEL = 'Announcements / Remarks'
export const ANNOUNCED_CONDITIONS_LABEL = '<b>Announced Conditions: </b>'
export const COMMENT_LINK_LABEL = '<b>Comment: </b>'
export const TMU_REASON_LABEL = '<b>TMU Reason: </b>'
export const AS_IS_100K_LABEL = '<b>As-Is 100K: </b>'
export const OPEN_RECALL_CAMPAIGN_ID_LABEL = 'Open Recall Campaign ID'
export const OPEN_RECALL_EFFECTIVE_DATE_LABEL = 'Effective Date'
export const OPEN_RECALL_DETAILS_LABEL = 'Recall Details'
export const OPEN_RECALL_CLEARING_MESSAGE_LABEL = '*The Open Recall must be cleared before retailing.'

export const Return_To_Dashboard = 'Return_To_Dashboard'

export const CHARGEABLE_DAMAGE = 'ChargeableDamage'
export const NON_CHARGEABLE_DAMAGE = 'NonChargeableDamage'
export const UNKNOWN_CHARGEABLE_DAMAGE = 'UnknownChargeableDamage'

export const GND_DISPOSITION_RETURN_CONFIRM =
  'You have indicated that the Lessee/Customer is returning their lease vehicle, please select from the following statements:'
export const GND_DISPOSITION_PURCHASE_CONFIRM =
  'You have indicated the dealer is purchasing a lease vehicle. Please select one of the following statements that represents the lessee’s new contract:'

export const GENERAL_ERROR_MESSAGE = 'Required'
export const ERROR_ODOMETER_REQUIRED_MESSAGE = 'Odometer Reading is required'
export const ERROR_ODOMETER_INVALID_INPUT = 'Please enter valid odometer reading'
export const ODOMETER_WARNING = 'Mileage entered is less than mileage on preterm inspection'

export const ERROR_VIN_REQUIRED_MESSAGE = 'Required'
export const ERROR_VIN_INVALID_LENGTH_MESSAGE = 'VIN must be 17 characters'
export const ERROR_VIN_INVALID_INPUT_MESSAGE = 'You have entered an invalid VIN. Please re-enter.'

export const ERROR_EMAILS_REQUIRED_MESSAGE = 'You must enter one or more email address(es)'
export const ERROR_EMAILS_INVALID_INPUT_MESSAGE = 'Invalid email addess(es), please re-enter valid email address(es)'
export const ERROR_EMAILS_LIST_INVALID_INPUT_MESSAGE =
  'Email address list is invalid. Please enter a valid list of emails, seperated by semi-colons'

export const ERROR_LESSEE_SIGNATURE_REQUIRED_MESSAGE = 'Lessee Signature is required'
export const ERROR_RETAILER_SIGNATURE_REQUIRED_MESSAGE = 'Retailer Signature is required'
export const CLIENT_CARE_EMAIL = 'Client_Care_Email'
export const READY_LOGISTIC_TRANSPORT_ID = 1
export const READY_LOGISTIC_LINK = 'https://www.readylogistics.com'
export const READY_TOOLTIP = 'If you have any questions, please contact Ready Logistics at 1-480-729-8793.'
export const DELIVERY_INFORMATION_NOT_AVAILABLE = 'Delivery Information Not Available'
export const SELF_ARRANGED_TRANSPORT_PROVIDER_ID = 3
export const OTHER_TRANSPORT_PROVIDER_ID = 2
export const PURCHASE_EXCLUSIVE_SUCCESS_MESSAGE = 'Vehicle has been successfully purchased.'
export const SOLD_VEHICLE_TAG = 'SOLD'
// Initial query payload for the SearchVehicle request
export const AVAILABLE_INVENTORY_INIT_QUERY: ISearchVehiclesRequest = {
  PageNumber: 0,
  PageSize: 25,
  QuickSearch: false,
  ReturnVehicles: false,
  ReturnZeroCountFilters: false,
  Ids: [],
  FilterOptions: {
    IsReserveMetVehicles: false,
    IsRecentlyAddedVehicles: false
  },
  SearchQuery: {
    ReturnOnlyVehicles: false,
    SearchFilters: [],
    SortBy: [],
    StatusIds: []
  }
}

export const SRP_INIT_QUERY: ISearchVehiclesRequest = {
  ...AVAILABLE_INVENTORY_INIT_QUERY,
  ReturnVehicles: true,
  SearchQuery: {
    ...AVAILABLE_INVENTORY_INIT_QUERY.SearchQuery,
    ReturnOnlyVehicles: true
  }
}

export const SRP_FILTERS_QUERY: Omit<typeof AVAILABLE_INVENTORY_INIT_QUERY, 'FilterOptions'> = {
  ...AVAILABLE_INVENTORY_INIT_QUERY
}

// Initial query payload for the GetHistoricalDealerPurchaseHistoryReport request.
export const PURCHASE_HISTORY_INIT_QUERY: IPurchaseHistoryRequest = {
  FromDate: DateTime.now().minus({ month: 1 }).toJSDate(),
  ToDate: DateTime.now().toJSDate(),
  Vin: null,
  PageNumber: 0,
  PageSize: DEFAULT_PAGE_SIZE,
  SortBy: {
    Name: 'Purchase Date - ↓',
    Value: 'PURCHASEDATE DESC',
    SortOrder: 2,
    DBFieldName: 'Purchase Date'
  } as ISortBy
}

export const purchaseHistorySortOptions: ISortBy[] = [
  { Name: 'Purchase Date - ↓', Value: 'PURCHASEDATE DESC', SortOrder: SortOrder.DESC, DBFieldName: 'Purchase Date' },
  { Name: 'Purchase Date - ↑', Value: 'PURCHASEDATE ASC', SortOrder: SortOrder.ASC, DBFieldName: 'Purchase Date' },
  { Name: 'Title - ↓', Value: 'TITLE DESC', SortOrder: SortOrder.DESC, DBFieldName: 'Title' },
  { Name: 'Title - ↑', Value: 'TITLE ASC', SortOrder: SortOrder.ASC, DBFieldName: 'Title' },
  { Name: 'VIN - ↓', Value: 'VIN DESC', SortOrder: SortOrder.DESC, DBFieldName: 'VIN' },
  { Name: 'VIN - ↑', Value: 'VIN ASC', SortOrder: SortOrder.ASC, DBFieldName: 'VIN' },
  { Name: 'Total Price - ↓', Value: 'TOTALPRICE DESC', SortOrder: SortOrder.DESC, DBFieldName: 'Total Cost' },
  { Name: 'Total Price - ↑', Value: 'TOTALPRICE ASC', SortOrder: SortOrder.ASC, DBFieldName: 'Total Cost' },
  { Name: 'Price - ↓', Value: 'PRICE DESC', SortOrder: SortOrder.DESC, DBFieldName: 'Price' },
  { Name: 'Price - ↑', Value: 'PRICE ASC', SortOrder: SortOrder.ASC, DBFieldName: 'Price' },
  { Name: 'Payment Method - ↓', Value: 'PAYMENTMETHOD DESC', SortOrder: SortOrder.DESC, DBFieldName: 'Payment Method' },
  { Name: 'Payment Method - ↑', Value: 'PAYMENTMETHOD ASC', SortOrder: SortOrder.ASC, DBFieldName: 'Payment Method' }
]

export const MODEL_GROUP_MAPPER: { DisplayText: string; Value: string }[] = [
  { DisplayText: 'Hybrids', Value: 'Hybrid' }
]

export const AVAILABLE_INVENTORY_MAPPER: { DisplayText: string; Value: string }[] = [
  //Body Styles
  { DisplayText: 'Crossover & SUVs', Value: 'Sport Utility,Station Wagon' },
  { DisplayText: 'Cars', Value: '4dr Car' },
  {
    DisplayText: 'Trucks',
    Value:
      'Truck,Crew Cab Pickup,Crew Cab Pickup - Short Bed,Extended Cab Pickup,Full-size Cargo Van,Full-size Passenger Van,Mini-van, Cargo,Mini-van, Passenger,Regular Cab Pickup'
  },
  { DisplayText: 'Sport Cars', Value: '2dr Car' },

  //Fuel Types
  { DisplayText: 'Gasoline', Value: 'Gasoline,Diesel Fuel,Gasoline Fuel' },
  { DisplayText: 'Fully Electric', Value: 'Electric Fuel System,Plug-In Electric Fast Charge,Electric Fuel,Electric' },
  {
    DisplayText: 'Hybrid',
    Value: 'Gas/Electric Hybrid,Flex Fuel Capability,Flex Fuel,Gasoline/Electric Fuel,Gasoline/Electric'
  },

  { DisplayText: 'BodyStyle', Value: 'Body Style' },
  { DisplayText: 'FuelType', Value: 'Fuel Type' }
]

export const AVAILABLE_INVENTORY_HEADING: Record<string, string[]> = {
  BodyStyle: ['Crossover & SUVs', 'Cars', 'Trucks', 'Sport Cars'],
  FuelType: ['Gasoline', 'Hybrid', 'Fully Electric'],
  Make: ['Nissan', 'Infiniti']
}

export const GENERIC_NO_VEHICLE_AVAILABLE_MESSAGE = 'No vehicles available.'
export const NO_NEW_INVENTORY_MESSAGE = 'No new inventory.'

export const SessionStorageKey = {
  SEARCH_FILTERS: 'SearchFilters',
  SEARCH_DESCRIPTION: 'SearchDescription',
  SEARCH_ID: 'SearchID',
  QUERY_FILTERS: 'QueryFilters',
  PAGE_SELECTED: 'PageSelected',
  SESSION_SORT: 'SessionSort',
  PAGE_SIZE: 'PageSize',
  VEHICLE_INSTANCES: 'vehicleInstances',
  FILTER_OPTION: 'FilterOptions',
  LIST_BREADCRUMB: 'breadCrumb_Listing',
  DETAIL_BREADCRUMB: 'breadCrumb_Detail',
  SIGNAL_R_CONNECTION_TRY_RECOUNT: 'signalRConnectionRetryCount',
  USER_LOCATION: 'UserLocation',
  SCROLL_TO_VIN: 'ScrollToVin',
  VIEW_VEHICLE_LIST: 'ViewVehicleMode',
  PAGE_PATHNAME: 'PagePathname',
  GROUND_VIN: 'groundVIN',
  WELCOME_MODAL_SHOWN: 'welcomeModalIsShown',
  PURCHASE_HISTORY_SEARCH_FILTER: 'PurchaseHistoryFilter'
}

export const assetFolders = {
  Infiniti: 'Infiniti',
  Independent: 'Independent',
  Nissan: 'Nissan',
  Guest: undefined
}

export const ImageType = {
  DAMAGE_IMAGE: 'Damage Image'
}

export const DefaultFilterOptions = {
  IsReserveMetVehicles: false,
  IsRecentlyAddedVehicles: false
}

export const zIndex = {
  elevated: 10,
  floatingControls: 2, // This layer stays above Bootstrap custom controls and carousel navigators
  pageHeader: 3,
  menu: 20,
  header: 1030,
  subMenu: 1060,
  stickyHeader: 1020,
  stickyAlert: 1020,
  modal: 1050,
  navbarMobile: 1300,
  loader: 1400
  // carousel-indicators: 15
} as const

export const VehicleStatuses = {
  SoldNotFunded: 4,
  OnSale: 36,
  InTransit: 34,
  Inspection: 25,
  AwaitingPricing: 1,
  AwaitingConsignment: 37,
  AuctionRollOff: 30,
  GroundedExclusive: 43
}
export const VehicleOfferStatuses = {
  OfferCountered: 3
}

export const VehicleOffers = {
  AWAITING: 1
}

export const MenuExclusionList = [{ name: 'RDP_MyAccount', childrens: ['RDP_MyProfile'] }]
export const FooterMenuExclusionList = ['RDP_ContactUs']

export const vendorImages = {
  rightLogo: '/content/custom/NISUS001/{assetFolder}/logo-right.png',
  whiteLogo: '/content/custom/NISUS001/{assetFolder}/logo-right-white.png',
  noModelImage: '/content/img/AwaitingCarImage.png'
}

export const enum BaseVehicleSearchCriteriaColumn {
  Make = 1,
  Model = 3,
  VIN = 6,
  ModelYear = 79,
  Proximity = 1016,
  Promotion = 1027,
  NewInventory = 1028,
  DecreasedPrice = 1029,
  DisclosurePresent = 1030,
  OdometerCustomRange = 1032,
  VehicleConditionCode = 1033,
  DriveTrain = 1019
}

export const MIN_MATCH_CHAR_LENGTH = 2

export const DefaultSortBy = {
  // Unset the name by default, so that we can query
  // and using the name returned from the API
  //
  // This will prevent hardcoded name not match with user's language pref
  Name: 'Odometer - ↑',
  Value: 'Mileage ASC',
  SortOrder: 1,
  DBFieldName: 'Mileage'
}

export const carfaxIcon = {
  carfaxTitleLogo: '/content/img/partners/carfax-title-logo.png',
  carfaxBaseUrl: '/content/img/partners/'
}

export const BUY_VEHICLE_NO_ACTIVITY = {
  WATCH_LIST: 'Once you add vehicles to your watchlist, they will appear here.',
  CURRENT_BIDS: 'Once you place bids on vehicles, they will appear here.',
  SAVE_SEARCH: 'You do not have any searches currently saved.',
  SEARCH_RESULTS: 'We were not able to find any vehicles that matched your criteria.'
}

export const exportViews = {
  Excel: 1,
  PDF: 2
}
export const SELECT_DISPLAY_LIMIT = 3
export const SelfArrangedTransportProviderId = 3

export enum BidAmountTypeOptions {
  CurrentBidAmount = 'CurrentBidAmount',
  UpdateBidAmount = 'UpdateBidAmount'
}

export const BuyItNowPrice = 99999

export const enum VehicleDetailsView {
  List = 'list',
  Grid = 'grid'
}

export const EnterKeyCharCode = 13

export const enum TyreStatus {
  GOOD = 'good',
  NORMAL = 'normal',
  BAD = 'bad'
}
export const enum Tyres {
  FRONT_LEFT_TIRE,
  FRONT_RIGHT_TIRE,
  REAR_LEFT_TIRE,
  REAR_RIGHT_TIRE,
  SPARE_TIRE
}

export const TyresDescriptionValue = {
  [Tyres.FRONT_LEFT_TIRE]: ['FRONT LEFT TIRE', 'LFRNT', 'LEFT FRONT'],
  [Tyres.FRONT_RIGHT_TIRE]: ['FRONT RIGHT TIRE', 'RFRNT', 'RIGHT FRONT'],
  [Tyres.REAR_LEFT_TIRE]: ['REAR LEFT TIRE', 'LREAR', 'LEFT REAR'],
  [Tyres.REAR_RIGHT_TIRE]: ['REAR RIGHT TIRE', 'RREAR', 'RIGHT REAR'],
  [Tyres.SPARE_TIRE]: ['SPARE TIRE', 'SPARE']
}

export const TireTreadDepthRange = [4, 6, 32]

export const enum WidgetConfigurationDetails {
  WIDGET_SPECIFICS = 'Widget_Specifics',
  WIDGET_EQUIPMENT = 'Widget_Equipment',
  WIDGET_DAMAGEUPSTREAM = 'Widget_DamageUpstream',
  WIDGET_DAMAGEGROUNDEDEXCLUSIVE = 'Widget_DamageGroundedExclusive',
  WIDGET_PROMOTIONS = 'Widget_Promotions',
  WIDGET_ANNOUNCEMENTS = 'Widget_Announcements',
  WIDGET_PACKAGES = 'Widget_Packages',
  WIDGET_HISTORY = 'Widget_History',
  WIDGET_CONDITION = 'Widget_Condition',
  WIDGET_DAMAGE = 'Widget_Damage',
  WIDGET_TIRE = 'Widget_Tire',
  WIDGET_ADDITIONAL = 'Widget_Additional',
  WIDGET_DISCLAIMER = 'Widget_Disclaimer',
  WIDGET_BATTERYHEALTH = 'Widget_BatteryHealth'
}

export const DEFAULT_EXPAND_FILTERS_NUMBER = 10
export const fullScreenIcon = {
  zoomOutLogo: '/content/img/zoom_out.png',
  zoomInLogo: '/content/img/zoom_in.png'
}

export const awaitingCarImage = {
  Nissan: '/content/img/NissanAwaitingCarImage.png',
  Infiniti: '/content/img/InfinitiAwaitingCarImage.png',
  Default: '/content/img/AwaitingCarImage.png'
}

export const SELECTED_SEARCH_FILTERS_LIMIT = 5

export const PROXIMITY_FILTERS = [99999, 25, 50, 100, 200, 500, 1000]

export const enum RangeFieldIds {
  PROXIMITY_FIELD_ID = 1016,
  ODOMETERCUSTOMRANGE_FIELD_ID = 1032,
  VEHICLECONDITONCODE_FIELD_ID = 1033
}

export const RANGE_FIELD_ID = [
  RangeFieldIds.ODOMETERCUSTOMRANGE_FIELD_ID,
  RangeFieldIds.PROXIMITY_FIELD_ID,
  RangeFieldIds.VEHICLECONDITONCODE_FIELD_ID
]

export const LogoFooter = '/content/img/Private-Store-by-Manheim.png'

export const MY_ACTIVITY_YTD = {
  GROUNDED: 'Grounded',
  GROUNDED_PURCHASED: 'Grounded Purchased',
  WEB_AUCTION_PURCHASED: 'Web Auction Purchased'
}

export const GROUNDING_OVERVIEW_SECTION = {
  EXPIRING_SOON: 'Expiring Soon',
  EXPIRING_TODAY: 'Expiring Today',
  GROUNDING_OVERDUE: 'Overdue Mandantory Purchases',
  PRIVATE_EXCLUSIVE_MARKET: 'Private Exclusive Market',
  AWAITING_INSPECTION: 'Awaiting Inspection',
  AWAITING_PRICING: 'Awaiting Pricing'
}

export const DashboardGrounding = {
  PENDING_OFFERS: 0,
  GROUNDING_EXPIRING_SOON: 1,
  GROUNDING_EXPIRING_TODAY: 2,
  GROUNDING_OVERDUE: 3,
  GROUNDING_AWAITING_PRICING: 4,
  GROUNDING_AWAITING_INSPECTION: 5
}

export const enum AddressTypes {
  HEAD_OFFICE = 1,
  INVOICE = 2,
  BRANCH_OFFICE = 3,
  GENERAL = 4,
  DEFLEET_AGENT = 5,
  COLLECTION = 6,
  AUCTION_CENTER = 7,
  DELIVERY = 8,
  TITLE_ADDRESS = 9,
  VEHICLE_PICKUP_LOCATION = 10,
  SELF_COLLECT = 11,
  USER_ADDRESS = 12,
  UPSTREAM_LOCATION = 13,
  SELLER_DISCLOSURE = 15,
  ALTERNATE_LOCATION = 16,
  CORPORATE_OFFICE = 17
}

export enum RecallDetailStatus {
  HasOpenRecall = 1,
  NoOpenRecall = 2,
  NoData = 3
}

export const enum SaveLocationOption {
  SAVE_TO_BUYER = 1,
  NOT_SAVE_TO_BUYER = 2
}

export const BUYER_TYPE_ID = {
  NISSAN_FRANCHISED: 907,
  INFINITI_FRANCHISED: 908,
  MDM: 909,
  INDEPENDENT: 911,
  NISSAN_FRANCHISE_WITH_AA: 912,
  INFINITI_FRANCHISE_WITH_AA: 913,
  AUCTION: 914,
  CORPORATE_OFFICE_UPSTREAM: 915,
  CORPORATE_OFFICE_NON_UPSTREAM: 916
}
export const VIN_CHARACTERS_REGEX = new RegExp(/[a-zA-Z0-9]/)

export const enum DocumentTypeEnum {
  TURNIN = 12,
  PURCHASE = 15,
  PAYOFF = 18
}

export const DocumentType = [
  {
    id: DocumentTypeEnum.TURNIN,
    description: 'turn-in odometer statement'
  },
  {
    id: DocumentTypeEnum.PURCHASE,
    description: 'purchase confirmation'
  },
  {
    id: DocumentTypeEnum.PAYOFF,
    description: 'payoff Authorization form'
  }
]

export const RetailViewEmailText = {
  EMAIL_TEMPLATE_NOT_FOUND: 'Failed to get email template. Please enter the content manually.',
  EMAIL_SENT_SUCCESS: 'Email sent successfully!',
  EMAIL_SENDING_FAILED: 'Failed to send email. Please try again.'
}

export const enum EVBatteryUnit {
  EVBattery_kWh = 'kWh',
  EVBattery_Years = 'Years',
  EVBattery_Miles = 'mi'
}
